/**
 * Client mirror of backend/src/utils/safeContinuePath.ts — keep the two
 * identical. A continuation (`continue` / `return_to`) is honoured only when it
 * resolves to this origin: a single leading '/', never '//' or '/\', no control
 * characters, and the path percent-decoded once must satisfy the same rule and
 * carry no scheme. Returns the input when safe, otherwise null; callers drop an
 * unsafe value rather than coercing it.
 */
const MAX_LENGTH = 2048;
const BACKSLASH = String.fromCharCode(92);

function hasControlChar(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const c = value.charCodeAt(i);
    if (c <= 0x1f || c === 0x7f) return true;
  }
  return false;
}

function isOriginRelativePath(value: string): boolean {
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//') || value.startsWith('/' + BACKSLASH)) return false;
  return true;
}

// A path segment that is '.' or '..' — literally or percent-encoded ('%2e'),
// which the WHATWG URL parser also treats as a dot segment. Dot segments are
// what turn an innocent-looking path into a scheme-relative one after
// normalisation: '/.//evil.example' and '/a/..//evil.example' both resolve to
// the pathname '//evil.example' (CEN-BILL-2).
function hasDotSegment(path: string): boolean {
  return path.split('/').some((segment) => segment === '.' || segment === '..');
}

// Never a real host: only used to resolve the candidate the way a browser
// would and to check that it stays on the origin it started from.
const PROBE_ORIGIN = 'https://continue-probe.invalid';

export function safeContinuePath(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  if (input.length === 0 || input.length > MAX_LENGTH) return null;
  if (input !== input.trim() || hasControlChar(input)) return null;
  if (!isOriginRelativePath(input)) return null;

  const path = input.split(/[?#]/, 1)[0] ?? '';
  if (path.includes(BACKSLASH)) return null;
  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(path);
  } catch {
    return null;
  }
  if (hasControlChar(decodedPath) || !isOriginRelativePath(decodedPath)) return null;
  if (decodedPath.includes(BACKSLASH) || decodedPath.includes('://')) return null;
  // No empty segments and no dot segments anywhere in the path, raw or
  // decoded: nothing may normalise into a leading '//'.
  if (path.includes('//') || decodedPath.includes('//')) return null;
  if (hasDotSegment(path) || hasDotSegment(decodedPath)) return null;

  // Belt and braces: resolve exactly as a browser would and require that the
  // result is still on the probe origin with a non scheme-relative pathname.
  let resolved: URL;
  try {
    resolved = new URL(input, PROBE_ORIGIN);
  } catch {
    return null;
  }
  if (resolved.origin !== PROBE_ORIGIN) return null;
  if (resolved.pathname.startsWith('//') || resolved.pathname.includes(BACKSLASH)) return null;
  return input;
}

/**
 * True when a continuation (already accepted by safeContinuePath) points into
 * the /auth/* area. Compared on the percent-decoded, lower-cased path: the SPA
 * router matches decoded paths case-insensitively, so '/%61uth/accept-invite'
 * and '/AUTH/accept-invite' reach the same page as '/auth/accept-invite' and a
 * raw `startsWith('/auth/')` would miss them. Undecodable input counts as
 * /auth/* (fail closed).
 */
export function isAuthAreaPath(continuation: string): boolean {
  const path = continuation.split(/[?#]/, 1)[0] ?? '';
  let decoded: string;
  try {
    decoded = decodeURIComponent(path);
  } catch {
    return true;
  }
  const lower = decoded.toLowerCase();
  return lower === '/auth' || lower.startsWith('/auth/');
}
